import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { IBAN_COMPONENT_ELEMENT_ID } from '@dealstack/shared/tests/testElementIds.ts';
import { BankIdentifierValidationType } from '@dealstack/shared/lib/generated';
import type { BankAccountFormatRuleRes } from '@dealstack/shared/lib/generated';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { Select, TextInput } from 'react-hook-form-mantine';
import { useBankAccountFormContext } from '../../BankAccountFormContext.tsx';
import { useBankAccountVerificationContext } from '../../BankAccountVerificationContext.tsx';
import { BANK_ACCOUNT_FORM_FIELDS, BankAccountFormValues } from '../../types.ts';
import { RuleVerificationPanel } from './RuleVerificationPanel.tsx';
import { SummaryCard } from '@dealstack/frontend/shared/components/SummaryCard/SummaryCard.tsx';
import { getStoredSummaryRows } from './FormatRuleFieldset.utils.ts';

// Labels for the stored rows; the same copy the lookup summary uses.
const LABEL_KEY_BY_IDENTIFIER: Record<string, string> = {
	bank_name: 'bank_account.drawer.bank_name.label',
	account_number: 'bank_account.drawer.account_number.label',
	bic: 'bank_account.drawer.bic.label',
};

interface Props {
	rule: BankAccountFormatRuleRes;
}

// Renders the fields declared by a BankAccountFormatRule; labels and placeholders
// come from the rule config itself. A field with `options` renders as a select.
// An IBAN-carrying rule never reaches here — it renders the classic IBAN form.
export const FormatRuleFieldset = ({ rule: formatRule }: Props) => {
	const tCapManage = useTranslationCapManage();
	const { control } = useFormContext<BankAccountFormValues>();
	const { fieldsDisabled, bankAccount, storedSummaryValidated } = useBankAccountFormContext();
	const verification = useBankAccountVerificationContext();

	const showVerification = formatRule.validationType !== BankIdentifierValidationType.NONE;

	// Stored values this tab renders no input for. Lives here rather than in the
	// verification panel so it also reaches the formats with no external lookup, which
	// never mount that panel. A live result supersedes it.
	const storedRows =
		verification.status === 'idle'
			? getStoredSummaryRows(
					bankAccount?.identifiers,
					new Set(formatRule.fields.map((field) => field.key)),
					(key) => tCapManage(LABEL_KEY_BY_IDENTIFIER[key] ?? key)
				)
			: [];

	return (
		<>
			{formatRule.fields.map((field) => {
				const name = `${BANK_ACCOUNT_FORM_FIELDS.identifiers}.${field.key}` as const;
				const testId = `${IBAN_COMPONENT_ELEMENT_ID.BANK_DYNAMIC_FIELD_INPUT}_${field.key}`;
				const isFixedValue = field.value !== undefined;

				if (field.options?.length && !isFixedValue) {
					return (
						<Select
							key={field.key}
							control={control}
							name={name}
							label={field.label}
							placeholder={field.placeholder}
							withAsterisk={field.required}
							size="md"
							data={field.options.map((option) => ({
								value: option,
								label: option,
							}))}
							allowDeselect={!field.required}
							disabled={fieldsDisabled}
							data-testid={testId}
						/>
					);
				}

				return (
					<TextInput
						key={field.key}
						control={control}
						name={name}
						label={field.label}
						placeholder={field.placeholder}
						withAsterisk={field.required}
						size="md"
						disabled={fieldsDisabled || isFixedValue}
						data-testid={testId}
					/>
				);
			})}

			{storedRows.length > 0 && (
				<SummaryCard
					title={tCapManage('bank_account.drawer.saved_details.title')}
					rows={storedRows}
					// These rows are a lookup's output (a GB sort code check stores its
					// bank name and BIC unlinked, since the country's IBAN rule declares
					// neither), so they carry the same outline as any other verified
					// summary — and lose it when the country or format moves on.
					highlighted={storedSummaryValidated}
					dataTestId={IBAN_COMPONENT_ELEMENT_ID.BANK_RULE_STORED_SUMMARY}
				/>
			)}

			{showVerification && <RuleVerificationPanel />}
		</>
	);
};
